import React, { useEffect } from "react";
import * as d3 from "d3";
import PropTypes from "prop-types";

const RECT_WIDTH = 16;
const RECT_HEIGHT = 12;
const TEXT_X = 24;
const TEXT_Y = 12;
const SPACING = 12;
const ROW_HEIGHT = 24;
const PADDING = 12;

const Key = ({ colorScale, data, forwardedRef, margin, width }) => {
  useEffect(() => {
    const svg = d3.select(forwardedRef.current);
    svg.selectAll("*").remove(); // Clear the container

    const legendContainer = svg
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    let cumulativeWidth = 0; // Track cumulative width of legend items
    let row = 0; // Track current row

    data.forEach((dataset, index) => {
      // Create group for each legend item
      const legendItem = legendContainer
        .append("g")
        .attr("class", "legend-item");

      // Append rectangle to legend item
      legendItem
        .append("rect")
        .attr("width", RECT_WIDTH)
        .attr("height", RECT_HEIGHT)
        .attr("y", 2)
        .attr("fill", colorScale(dataset.name));

      // Append text next to rectangle
      const textElement = legendItem
        .append("text")
        .attr("x", TEXT_X)
        .attr("y", TEXT_Y) // Adjust for vertical alignment
        .attr("fill", "currentColor")
        .style("font-size", "0.8em")
        .attr("dy", "0.1em")
        .text(dataset.name);

      // Wait for text to be rendered to measure width
      const textWidth = textElement.node().getBBox().width;
      const totalWidth = RECT_WIDTH + SPACING + textWidth; // Rect width + spacing + text width

      // Wrap to next row if exceeding max width
      if (cumulativeWidth + totalWidth > width) {
        row++;
        cumulativeWidth = 0;
      }

      // Position legend item
      legendItem.attr(
        "transform",
        `translate(${cumulativeWidth}, ${row * ROW_HEIGHT})`
      );

      // Update cumulative width
      cumulativeWidth += totalWidth + PADDING; // Add some padding between items
    });

    // Optionally adjust the SVG height based on the number of rows
    const adjustedHeight = (row + 1) * ROW_HEIGHT;
    svg.attr("height", adjustedHeight + margin.top + margin.bottom);
  }, [data, colorScale, margin, width, forwardedRef]);

  return <g ref={forwardedRef} />;
};

Key.propTypes = {
  data: PropTypes.array.isRequired,
  colorScale: PropTypes.func.isRequired,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.node })
  ]).isRequired,
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number
  }),
  width: PropTypes.number
};

Key.defaultProps = {
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  width: 500
};

export default Key;
